/**
=========================================================
* Application Status Check Component
=========================================================
*/

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Typography,
  Chip,
  Grid,
} from "@mui/material";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";

// Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import PhoneIcon from "@mui/icons-material/Phone";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import TimerIcon from "@mui/icons-material/Timer";

// API service
import { publicRegistrationAPI } from "services/api";

function ApplicationStatusCheck({ publicRegistrationCode }) {
  // Form state
  const [mobileNo, setMobileNo] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("mobile"); // mobile, otp, status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Status data
  const [statusData, setStatusData] = useState(null);
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [otpExpiryTime, setOtpExpiryTime] = useState(null);
  const [otpCountdown, setOtpCountdown] = useState(null);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(null);

  // OTP countdown timer (10 minutes)
  useEffect(() => {
    if (otpExpiryTime && step === "otp") {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const expiry = new Date(otpExpiryTime).getTime();
        const diff = expiry - now;
        
        if (diff > 0) {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setOtpCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        } else {
          setOtpCountdown(null);
          setOtpExpiryTime(null);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [otpExpiryTime, step]);

  // Rate limit countdown timer
  useEffect(() => {
    if (rateLimitInfo?.time_remaining_seconds && rateLimitInfo.time_remaining_seconds > 0) {
      const interval = setInterval(() => {
        setRateLimitInfo(prev => {
          if (prev?.time_remaining_seconds > 0) {
            return {
              ...prev,
              time_remaining_seconds: prev.time_remaining_seconds - 1
            };
          }
          return null;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [rateLimitInfo]);

  // Format rate limit time
  const formatRateLimitTime = (seconds) => {
    if (!seconds) return "";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  // Validate mobile number
  const validateMobile = (mobile) => {
    const cleaned = mobile.replace(/\D/g, "");
    return cleaned.length === 10 && /^\d{10}$/.test(cleaned);
  };

  // Handle send OTP
  const handleSendOTP = async () => {
    const cleanedMobile = mobileNo.replace(/\D/g, "");
    
    if (!validateMobile(cleanedMobile)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setRateLimitInfo(null);

      const response = await publicRegistrationAPI.sendApplicationStatusOTP({
        public_registration_code: publicRegistrationCode,
        mobile_no: cleanedMobile,
      });

      if (response.data.status === 1) {
        setSuccess(response.data.message || "OTP sent successfully to your mobile number");
        setRemainingAttempts(response.data.remaining_attempts || 0);
        setStep("otp");
        // Set OTP expiry (10 minutes from now)
        const expiryTime = new Date(Date.now() + 10 * 60 * 1000);
        setOtpExpiryTime(expiryTime.toISOString());
      } else {
        // Check if it's a rate limit error
        if (response.data.time_remaining_seconds !== undefined) {
          setRateLimitInfo({
            message: response.data.message,
            next_allowed_time: response.data.next_allowed_time,
            time_remaining_seconds: response.data.time_remaining_seconds,
          });
          setError(response.data.message);
        } else {
          setError(response.data.message || "Failed to send OTP");
        }
      }
    } catch (err) {
      console.error("Error sending OTP:", err);
      const errorMessage = err.response?.data?.message || "Failed to send OTP. Please try again.";
      
      // Check if it's a rate limit error
      if (err.response?.data?.time_remaining_seconds !== undefined) {
        setRateLimitInfo({
          message: errorMessage,
          next_allowed_time: err.response.data.next_allowed_time,
          time_remaining_seconds: err.response.data.time_remaining_seconds,
        });
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    if (remainingAttempts === 0) {
      setError("No more OTP requests remaining. Please wait and try again later.");
      return;
    }
    await handleSendOTP();
  };

  // Handle verify OTP
  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 4) {
      setError("Please enter a valid 4-digit OTP");
      return;
    }

    const cleanedMobile = mobileNo.replace(/\D/g, "");

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await publicRegistrationAPI.verifyApplicationStatusOTP({
        public_registration_code: publicRegistrationCode,
        mobile_no: cleanedMobile,
        otp: otp.trim(),
      });

      if (response.data.status === 1) {
        setStatusData(response.data);
        setStep("status");
        setOtpCountdown(null);
        setOtpExpiryTime(null);
      } else {
        setError(response.data.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setError(err.response?.data?.message || "Failed to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle start over
  const handleStartOver = () => {
    setMobileNo("");
    setOtp("");
    setStep("mobile");
    setError(null);
    setSuccess(null);
    setStatusData(null);
    setRemainingAttempts(null);
    setRateLimitInfo(null);
    setOtpCountdown(null);
    setOtpExpiryTime(null);
  };

  // Get status icon and color
  const getStatusDisplay = (status) => {
    switch (status) {
      case "approved":
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 60, color: "success.main" }} />,
          color: "success",
          text: "Application Approved",
          bgColor: "rgba(76, 175, 80, 0.1)",
        };
      case "pending":
        return {
          icon: <PendingIcon sx={{ fontSize: 60, color: "warning.main" }} />,
          color: "warning",
          text: "Application Pending",
          bgColor: "rgba(255, 152, 0, 0.1)",
        };
      case "rejected":
        return {
          icon: <CancelIcon sx={{ fontSize: 60, color: "error.main" }} />,
          color: "error",
          text: "Application Rejected",
          bgColor: "rgba(244, 67, 54, 0.1)",
        };
      case "not_there":
        return {
          icon: <InfoIcon sx={{ fontSize: 60, color: "info.main" }} />,
          color: "info",
          text: "No Application Found",
          bgColor: "rgba(33, 150, 243, 0.1)",
        };
      default:
        return {
          icon: <InfoIcon sx={{ fontSize: 60, color: "text.main" }} />,
          color: "info",
          text: "Unknown Status",
          bgColor: "rgba(158, 158, 158, 0.1)",
        };
    }
  };

  const statusDisplay = statusData ? getStatusDisplay(statusData.application_status) : null;

  return (
    <Card elevation={2}>
      <CardContent sx={{ p: 3 }}>
        <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2}>
          Check Application Status (അപേക്ഷ സ്റ്റാറ്റസ് പരിശോധിക്കുക)
        </SoftTypography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
            {rateLimitInfo && rateLimitInfo.time_remaining_seconds > 0 && (
              <Box mt={1}>
                <Typography variant="body2">
                  <TimerIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                  Retry after: <strong>{formatRateLimitTime(rateLimitInfo.time_remaining_seconds)}</strong>
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Step 1: Mobile Number Input */}
        {step === "mobile" && (
          <Box>
            <SoftBox mb={3}>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                color="dark"
                sx={{ mb: 1, display: "block" }}
              >
                Mobile Number (മൊബൈൽ നമ്പർ) *
              </SoftTypography>
              <SoftInput
                placeholder="Enter 10-digit mobile number (10 അക്ക മൊബൈൽ നമ്പർ നൽകുക)"
                value={mobileNo}
                onChange={(e) => setMobileNo(e.target.value.replace(/\D/g, "").slice(0, 10))}
                inputProps={{ maxLength: 10 }}
                icon={{
                  component: <PhoneIcon />,
                  direction: "left",
                }}
                sx={{ height: "42px" }}
              />
            </SoftBox>
            
            <SoftButton
              variant="gradient"
              color="primary"
              size="large"
              fullWidth
              onClick={handleSendOTP}
              disabled={loading || !mobileNo || mobileNo.length !== 10 || (rateLimitInfo?.time_remaining_seconds > 0)}
              startIcon={loading ? <CircularProgress size={20} /> : <VpnKeyIcon />}
            >
              {loading ? "Sending OTP..." : "Send OTP (OTP അയയ്ക്കുക)"}
            </SoftButton>

            {rateLimitInfo && rateLimitInfo.time_remaining_seconds > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Rate limit exceeded. Please wait <strong>{formatRateLimitTime(rateLimitInfo.time_remaining_seconds)}</strong> before requesting OTP again.
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Step 2: OTP Input */}
        {step === "otp" && (
          <Box>
            <SoftBox mb={3}>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                color="dark"
                sx={{ mb: 1, display: "block" }}
              >
                Enter OTP (OTP നൽകുക) *
              </SoftTypography>
              <SoftInput
                placeholder="Enter 4-digit OTP (4 അക്ക OTP നൽകുക)"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                inputProps={{ maxLength: 4 }}
                icon={{
                  component: <VpnKeyIcon />,
                  direction: "left",
                }}
                sx={{ height: "42px" }}
              />
              {otpCountdown && (
                <SoftTypography variant="caption" color="text" sx={{ mt: 1, display: "block" }}>
                  <TimerIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                  OTP expires in: <strong>{otpCountdown}</strong>
                </SoftTypography>
              )}
            </SoftBox>

            <Box display="flex" gap={2}>
              <SoftButton
                variant="gradient"
                color="primary"
                size="large"
                fullWidth
                onClick={handleVerifyOTP}
                disabled={loading || !otp || otp.length !== 4}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {loading ? "Verifying..." : "Verify OTP (OTP സ്ഥിരീകരിക്കുക)"}
              </SoftButton>
            </Box>

            <Box mt={2} display="flex" gap={2}>
              <SoftButton
                variant="outlined"
                color="secondary"
                size="medium"
                onClick={handleResendOTP}
                disabled={loading || remainingAttempts === 0 || (rateLimitInfo?.time_remaining_seconds > 0)}
                fullWidth
              >
                {remainingAttempts === 0 
                  ? "No Resend Available" 
                  : `Resend OTP (${remainingAttempts || 0} attempts left)`}
              </SoftButton>
              <SoftButton
                variant="text"
                color="secondary"
                size="medium"
                onClick={handleStartOver}
                fullWidth
              >
                Change Mobile Number
              </SoftButton>
            </Box>
          </Box>
        )}

        {/* Step 3: Status Display */}
        {step === "status" && statusData && (
          <Box>
            <Box
              sx={{
                textAlign: "center",
                p: 4,
                borderRadius: 2,
                backgroundColor: statusDisplay.bgColor,
                mb: 3,
              }}
            >
              {statusDisplay.icon}
              <SoftTypography variant="h5" color={statusDisplay.color} fontWeight="bold" mt={2} mb={1}>
                {statusData.status_text || statusDisplay.text}
              </SoftTypography>
              <SoftTypography variant="body2" color="text">
                Mobile: {mobileNo}
              </SoftTypography>
            </Box>

            {statusData.applications && statusData.applications.length > 0 && (
              <Box>
                <Divider sx={{ my: 3 }} />
                <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2}>
                  Application Details (അപേക്ഷ വിവരങ്ങൾ)
                </SoftTypography>

                {/* Status Summary */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <Chip label={statusData.total_applications || 0} color="primary" size="medium" />
                      <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                        Total
                      </SoftTypography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <Chip label={statusData.approved_count || 0} color="success" size="medium" />
                      <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                        Approved
                      </SoftTypography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <Chip label={statusData.pending_count || 0} color="warning" size="medium" />
                      <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                        Pending
                      </SoftTypography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Applications List */}
                {statusData.applications.map((app, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <SoftTypography variant="h6" color="dark" fontWeight="bold">
                          {app.req_player_name || "N/A"}
                        </SoftTypography>
                        <Chip
                          label={app.actual_series_participant_id > 0 ? "Approved" : app.status === 1 ? "Pending" : "Rejected"}
                          color={app.actual_series_participant_id > 0 ? "success" : app.status === 1 ? "warning" : "error"}
                          size="small"
                        />
                      </Box>
                      <SoftTypography variant="caption" color="text" display="block">
                        Registration ID: {app.req_series_participant_id}
                      </SoftTypography>
                      
                      <SoftTypography variant="caption" color="text" display="block">
                        Registered on: {new Date(app.added_on).toLocaleString()}
                      </SoftTypography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <SoftButton
              variant="gradient"
              color="primary"
              size="large"
              fullWidth
              onClick={handleStartOver}
            >
              Check Another Number (മറ്റൊരു നമ്പർ പരിശോധിക്കുക)
            </SoftButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default ApplicationStatusCheck;

